import type { KeyboardEvent as ReactKeyboardEvent } from "react";
import type React from "react";

type Props = {
  nextKeys?: KeyboardEvent["code"][];
  backKeys?: KeyboardEvent["code"][];
  className?: React.HTMLAttributes<HTMLDivElement>["className"];
  children: ({
    onKeyDown,
  }: {
    onKeyDown: (
      e: ReactKeyboardEvent<HTMLInputElement | HTMLButtonElement>,
    ) => void;
  }) => React.ReactNode;
  submitOnLastInput?: boolean;
  loop?: boolean;
};

export const FormNavigation = ({
  nextKeys = ["Enter", "NumpadEnter", "NumpadAdd"],
  backKeys = ["NumpadSubtract"],
  submitOnLastInput = false,
  loop = false,
  className,
  children,
}: Props) => {
  const handleKeyDown = (
    e: ReactKeyboardEvent<HTMLInputElement | HTMLButtonElement>,
  ) => {
    // blur on escape
    if (e.key === "Escape") {
      e.preventDefault();
      e.currentTarget.blur();
      return;
    }

    if (nextKeys.includes(e.code)) {
      e.preventDefault();
      const form = e.currentTarget.closest("form");
      const inputs = form?.querySelectorAll(
        "input, select, textarea, button[type='submit']",
      );
      const activeElement = document.activeElement;
      const activeElementIndex = inputs
        ? Array.from(inputs).indexOf(activeElement as HTMLElement)
        : -1;
      const nextElement = inputs?.[activeElementIndex + 1];

      if (
        submitOnLastInput &&
        nextElement?.tagName.toLowerCase() === "button"
      ) {
        const buttonElement = nextElement as HTMLButtonElement;
        if (buttonElement.type === "submit") {
          buttonElement.click();
          return;
        }
      }

      if (!nextElement && loop) {
        const firstElement = inputs?.[0];
        (firstElement as HTMLElement)?.focus();
        return;
      }

      (nextElement as HTMLElement)?.focus();
    }
    if (backKeys.includes(e.code)) {
      const target = e.currentTarget as HTMLInputElement;
      if (
        target.type === "number" ||
        target.inputMode === "numeric" ||
        target.inputMode === "decimal"
      ) {
        // Let the default behavior happen (typing the minus sign)
        return;
      }
      e.preventDefault();
      const form = e.currentTarget.closest("form");
      const inputs = form?.querySelectorAll(
        "input, select, textarea, button[type='submit']",
      );
      const activeElement = document.activeElement;
      const activeElementIndex = inputs
        ? Array.from(inputs).indexOf(activeElement as HTMLElement)
        : -1;
      const nextElement = inputs?.[activeElementIndex - 1];

      if (!nextElement && loop) {
        const lastElement = inputs?.[inputs.length - 1];
        (lastElement as HTMLElement)?.focus();
        return;
      }

      (nextElement as HTMLElement)?.focus();
    }
  };

  return (
    <div className={className}>{children({ onKeyDown: handleKeyDown })}</div>
  );
};
